import * as fabric from 'fabric';
import { Point } from '../types';

/**
 * Canvas utility functions for Fabric.js operations
 */

// Type definitions for fabric.js compatibility
interface FabricPathOptions {
  fill?: string | fabric.Pattern | fabric.Gradient<any, any>;
  stroke?: string;
  strokeWidth?: number;
  strokeLineCap?: CanvasLineCap;
  strokeLineJoin?: CanvasLineJoin;
  strokeMiterLimit?: number;
  strokeDashArray?: number[];
  strokeDashOffset?: number;
  opacity?: number;
}

export const CANVAS_WIDTH = 1920;
export const CANVAS_HEIGHT = 1080;

/**
 * Initialize a Fabric.js canvas with default settings
 */
export function initializeCanvas(canvasElement: HTMLCanvasElement): fabric.Canvas {
  const canvas = new fabric.Canvas(canvasElement, {
    isDrawingMode: false,
    selection: false,
    renderOnAddRemove: false,
    enableRetinaScaling: true,
    skipOffscreen: true,
    preserveObjectStacking: true,
  });

  // Set default canvas size
  canvas.setDimensions({
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT
  });

  return canvas;
}

/**
 * Create a Fabric.js path object from points
 */
export function createFabricPath(
  pathData: string,
  options: Partial<FabricPathOptions> = {}
): fabric.Path {
  const path = new fabric.Path(pathData, {
    strokeLineCap: 'round',
    strokeLineJoin: 'round',
    selectable: false,
    evented: false,
    ...options
  });

  return path;
}

/**
 * Get object at specific point on canvas
 */
export function getObjectAtPoint(
  canvas: fabric.Canvas,
  point: Point,
  skipTypes: string[] = []
): fabric.Object | null {
  const objects = canvas.getObjects().reverse();
  
  for (const obj of objects) {
    if (skipTypes.includes(obj.type || '')) continue;
    
    if (obj.containsPoint(new fabric.Point(point.x, point.y))) {
      return obj;
    }
  }
  
  return null;
}

/**
 * Clear all drawing objects from canvas (preserve background)
 */
export function clearDrawingObjects(canvas: fabric.Canvas) {
  const objects = canvas.getObjects();
  const objectsToRemove = objects.filter(obj => {
    // Keep background images and question-related objects
    const customData = (obj as any).data;
    return customData?.isDrawing || 
           (!customData?.isBackground && !customData?.isQuestion);
  });

  objectsToRemove.forEach(obj => canvas.remove(obj));
  canvas.requestRenderAll();
}

/**
 * Calculate canvas scale based on container size
 */
export function calculateCanvasScale(
  containerWidth: number,
  containerHeight: number,
  canvasWidth: number = CANVAS_WIDTH,
  canvasHeight: number = CANVAS_HEIGHT
): number {
  const scaleX = containerWidth / canvasWidth;
  const scaleY = containerHeight / canvasHeight;
  return Math.min(scaleX, scaleY);
}

/**
 * Convert canvas coordinates to viewport coordinates
 */
export function canvasToViewport(
  point: Point,
  canvas: fabric.Canvas
): Point {
  const zoom = canvas.getZoom();
  const vpt = canvas.viewportTransform;
  
  if (!vpt) return point;

  return {
    x: point.x * zoom + vpt[4],
    y: point.y * zoom + vpt[5]
  };
}

/**
 * Convert viewport coordinates to canvas coordinates
 */
export function viewportToCanvas(
  point: Point,
  canvas: fabric.Canvas
): Point {
  const zoom = canvas.getZoom();
  const vpt = canvas.viewportTransform;

  if (!vpt) return point;

  return {
    x: (point.x - vpt[4]) / zoom,
    y: (point.y - vpt[5]) / zoom
  };
}

/**
 * Rescale existing canvas background image to fit current canvas dimensions
 * This ensures the background always fits properly after resize
 */
export function rescaleCanvasBackground(canvas: fabric.Canvas): void {
  const backgroundImage = canvas.backgroundImage;

  if (!(backgroundImage instanceof fabric.Image)) {
    return; // No background image to scale
  }

  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  // Skip if canvas dimensions are not ready yet
  if (canvasWidth < 100 || canvasHeight < 100) {
    console.warn('Canvas dimensions too small, skipping background rescale');
    return;
  }

  const imageWidth = backgroundImage.width || 1;
  const imageHeight = backgroundImage.height || 1;

  // Calculate scale to fit canvas while maintaining aspect ratio
  // Always calculate from original image dimensions
  const scale = Math.min(
    canvasWidth / imageWidth,
    canvasHeight / imageHeight
  );

  // Apply the calculated scale
  backgroundImage.set({
    scaleX: scale,
    scaleY: scale
  });

  // Store the scale for debugging
  (backgroundImage as any)._calculatedScale = scale;

  canvas.requestRenderAll();
}

/**
 * Set canvas background image
 */
export async function setCanvasBackground(
  canvas: fabric.Canvas,
  imageUrl: string | undefined,
  options: any = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Removed crossOrigin to match Whiteboard behavior and avoid CORS issues
    if(!imageUrl) 
      {
       return resolve();
      }
    img.onload = () => {
      const fabricImg = new fabric.Image(img, {
        selectable: false,
        evented: false,
        ...options
      });

      // Set the background image without scaling initially
      // The scaling will be handled by rescaleCanvasBackground
      canvas.backgroundImage = fabricImg;

      // Now apply proper scaling based on current canvas dimensions
      rescaleCanvasBackground(canvas);

      resolve();
    };
    
    img.onerror = (e) => {
      console.error('Image load error:', {
        url: imageUrl,
        error: e,
        img: img
      });
      reject(new Error(`Failed to load image from ${imageUrl}`));
    };
    
    img.src = imageUrl;
  });
}

/**
 * Export canvas as data URL
 */
export function exportCanvas(
  canvas: fabric.Canvas,
  format: 'png' | 'jpeg' = 'png',
  quality: number = 1
): string {
  return canvas.toDataURL({
    format,
    quality,
    multiplier: 1
  });
}

/**
 * Check if point is within canvas bounds
 */
export function isPointInCanvas(
  point: Point,
  canvas: fabric.Canvas
): boolean {
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  
  return point.x >= 0 && 
         point.x <= width && 
         point.y >= 0 && 
         point.y <= height;
}